import { StyleModel } from './StyleModel'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import katex from 'katex'
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'

const INVISIBLE = '\u200B'
const INVISIBLE_RE = /\u200B/g

let _selectAllActive = false
let _selectAllTimer: ReturnType<typeof setTimeout> | null = null

export const setSelectAllActive = () => {
	if (_selectAllTimer) clearTimeout(_selectAllTimer)
	_selectAllActive = true
	_selectAllTimer = setTimeout(() => {
		_selectAllActive = false
		_selectAllTimer = null
	}, 500)
}

function escapeHTML(text: string): string {
	const div = document.createElement('div')
	div.textContent = text
	return div.innerHTML
}

function preserveSpacesAfterDelete() {
	const sel = window.getSelection()
	if (!sel || sel.rangeCount === 0) return

	const range = sel.getRangeAt(0)
	if (!range.collapsed) return

	const node = range.startContainer
	const offset = range.startOffset

	if (node.nodeType === Node.TEXT_NODE) {
		const text = node.textContent || ''

		// 같은 텍스트 노드 내 연속 공백
		if (offset > 0 && offset < text.length && text[offset - 1] === ' ' && text[offset] === ' ') {
			node.textContent = text.substring(0, offset) + '\u00A0' + text.substring(offset + 1)
			const newRange = document.createRange()
			newRange.setStart(node, offset)
			newRange.collapse(true)
			sel.removeAllRanges()
			sel.addRange(newRange)
			return
		}

		// 텍스트 노드 끝에서 다음 텍스트 노드 시작과의 연속 공백
		if (offset === text.length && text.endsWith(' ')) {
			let nextText: Text | null = null
			let next: Node | null = node.nextSibling
			while (next) {
				if (next.nodeType === Node.TEXT_NODE) { nextText = next as Text; break }
				if (next.nodeType === Node.ELEMENT_NODE) { nextText = findFirstTextNode(next); break }
				next = next.nextSibling
			}
			if (nextText && (nextText.textContent || '').startsWith(' ')) {
				node.textContent = text.substring(0, text.length - 1) + '\u00A0'
				const newRange = document.createRange()
				newRange.setStart(node, node.textContent!.length)
				newRange.collapse(true)
				sel.removeAllRanges()
				sel.addRange(newRange)
				return
			}
		}

		// 텍스트 노드 시작에서 이전 텍스트 노드 끝과의 연속 공백
		if (offset === 0 && text.startsWith(' ')) {
			let prevText: Text | null = null
			let prev: Node | null = node.previousSibling
			while (prev) {
				if (prev.nodeType === Node.TEXT_NODE) { prevText = prev as Text; break }
				if (prev.nodeType === Node.ELEMENT_NODE) { prevText = findLastTextNode(prev); break }
				prev = prev.previousSibling
			}
			if (prevText && (prevText.textContent || '').endsWith(' ')) {
				node.textContent = '\u00A0' + text.substring(1)
				const newRange = document.createRange()
				newRange.setStart(node, 0)
				newRange.collapse(true)
				sel.removeAllRanges()
				sel.addRange(newRange)
				return
			}
		}
	}
}

function findFirstTextNode(node: Node): Text | null {
	if (node.nodeType === Node.TEXT_NODE) return node as Text
	for (let i = 0; i < node.childNodes.length; i++) {
		const found = findFirstTextNode(node.childNodes[i])
		if (found) return found
	}
	return null
}

function findLastTextNode(node: Node): Text | null {
	if (node.nodeType === Node.TEXT_NODE) return node as Text
	for (let i = node.childNodes.length - 1; i >= 0; i--) {
		const found = findLastTextNode(node.childNodes[i])
		if (found) return found
	}
	return null
}

export const useCopy = () => {
	function onCopy(event: ClipboardEvent) {
		const target = event.target as HTMLElement

		const tableBlock = target.closest('.editor--block[data-type="defaultTable"]')
		if (tableBlock) {
			const { dragList } = storeToRefs(useEditorStore())
			if (dragList.value.length >= 2) {
				return
			}
		}

		// 1. selection 범위 확인
		let selection = window.getSelection()
		let selectedTableImageFileBlocks: HTMLElement[] = []

		if (selection && selection.rangeCount > 0) {
			const range = selection.getRangeAt(0)

			// selection 범위 내의 모든 테이블/이미지/파일 블록 찾기
			const editor = document.getElementById('editor')
			if (editor) {
				const allSpecialBlocks = editor.querySelectorAll('.editor--block[data-type="defaultTable"], .editor--block[data-type="image"], .editor--block[data-type="file"]')
				allSpecialBlocks.forEach((block: any) => {
					// DOM 위치 기반 검사
					try {
						const blockContainer = block.closest('.editor--block__container') || block

							// Range와 블록의 위치 비교
						const isInRange = selection!.containsNode(blockContainer, true) || selection!.containsNode(block, true)

						// Ctrl+A 같은 에디터 전체 선택만 감지 (일부 선택은 제외)
						const rangeCommonAncestor = range.commonAncestorContainer
						const editorElement = document.getElementById("editor")
						const isEditorWideSelection = rangeCommonAncestor === editorElement || _selectAllActive

						if (isInRange || isEditorWideSelection) {
							selectedTableImageFileBlocks.push(block as HTMLElement)
						}
					} catch (e) {
					}
				})
			}
		}

		// 3. target이 속한 블록 확인
		const targetBlock = target.closest('.editor--block') as HTMLElement

		// 4. targetBlock이 테이블, 이미지, 파일이고 active인 경우
		// 단, 여러 블록이 선택된 경우는 제외 (Ctrl+A 등)
		const isSpecialBlockActive = targetBlock && targetBlock.classList.contains('active') &&
		    (targetBlock.dataset.type === 'defaultTable' || targetBlock.dataset.type === 'image' || targetBlock.dataset.type === 'file')

		if (isSpecialBlockActive && selectedTableImageFileBlocks.length === 0) {
			event.preventDefault()
			const container = document.createElement('div')
			const clonedBlock = targetBlock.cloneNode(true) as HTMLElement
			container.appendChild(clonedBlock)

			const processedHTML = processNode(container)
			const tempDiv = document.createElement('div')
			tempDiv.innerHTML = processedHTML
			const blockContainers = tempDiv.querySelectorAll('.editor--block__container')
			blockContainers.forEach(containerEl => {
				const parent = containerEl.parentNode
				if (parent) {
					while (containerEl.firstChild) {
						parent.insertBefore(containerEl.firstChild, containerEl)
					}
					parent.removeChild(containerEl)
				}
			})
			const finalHTML = tempDiv.innerHTML
			const plainText = tempDiv.textContent?.replace(INVISIBLE_RE, '') || ''

			if (event.clipboardData) {
				event.clipboardData.setData('text/html', finalHTML)
				event.clipboardData.setData('text/plain', plainText)
			}
			return
		}

		// 5. selection이 있는 경우 처리
		selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) {
			return
		}

		const range = selection.getRangeAt(0)
		// 디버깅 추가

		// 특수 블록이 감지되었으면 collapsed 무시 (브라우저가 selection을 초기화했을 수 있음)
		if (range.collapsed && selectedTableImageFileBlocks.length === 0) {
			return
		}

		event.preventDefault()

		const container = document.createElement('div')

		// commonAncestorContainer가 TEXT_NODE인 경우 부모 요소 확인
		let ancestorElement = range.commonAncestorContainer as HTMLElement
		if (ancestorElement.nodeType === Node.TEXT_NODE) {
			ancestorElement = ancestorElement.parentElement as HTMLElement
		}

		// ancestorElement가 span이고 .editor--block 내부이면 span을 포함하여 복사
		const editorBlock = ancestorElement?.closest('.editor--block')
		const isInsideBlock = !!editorBlock
		const isSpanElement = ancestorElement?.tagName === 'SPAN'

		// 여러 블록이 선택된 경우 또는 전체 블록이 선택된 경우 처리
		const startContainer = range.startContainer
		const endContainer = range.endContainer
		let startBlock = (startContainer.nodeType === Node.ELEMENT_NODE ? (startContainer as HTMLElement) : (startContainer.parentElement as HTMLElement))?.closest('.editor--block')
		let endBlock = (endContainer.nodeType === Node.ELEMENT_NODE ? (endContainer as HTMLElement) : (endContainer.parentElement as HTMLElement))?.closest('.editor--block')


		// selectedTableImageFileBlocks가 있으면 실제 선택 범위 재계산
		if (selectedTableImageFileBlocks.length > 0) {
			const allBlocks = Array.from(document.querySelectorAll('.editor--block'))
			const allSelectedBlocks = [...selectedTableImageFileBlocks]

			// startBlock과 endBlock이 있으면 포함
			if (startBlock && !allSelectedBlocks.includes(startBlock as HTMLElement)) {
				allSelectedBlocks.push(startBlock as HTMLElement)
			}
			if (endBlock && !allSelectedBlocks.includes(endBlock as HTMLElement)) {
				allSelectedBlocks.push(endBlock as HTMLElement)
			}


			const indices = allSelectedBlocks
				.map(block => allBlocks.indexOf(block as Element))
				.filter(idx => idx !== -1)


			if (indices.length > 0) {
				const minIdx = Math.min(...indices)
				const maxIdx = Math.max(...indices)
				startBlock = allBlocks[minIdx] as HTMLElement
				endBlock = allBlocks[maxIdx] as HTMLElement
			}
		}

		// 테이블 셀 내부 텍스트 선택 여부 확인
		const startElement = startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentElement : startContainer as HTMLElement
		const endElement = endContainer.nodeType === Node.TEXT_NODE ? endContainer.parentElement : endContainer as HTMLElement
		const startCell = startElement?.closest('td')
		const endCell = endElement?.closest('td')
		const isWithinSingleCell = startCell && endCell && startCell === endCell


		// 여러 블록이 선택된 경우
		if (startBlock && endBlock && startBlock !== endBlock) {
			const allBlocks = Array.from(document.querySelectorAll('.editor--block'))
			const startIdx = allBlocks.indexOf(startBlock as Element)
			const endIdx = allBlocks.indexOf(endBlock as Element)

			if (startIdx !== -1 && endIdx !== -1) {
				const selectedBlocks = allBlocks.slice(Math.min(startIdx, endIdx), Math.max(startIdx, endIdx) + 1)
				const hasSpecialBlock = selectedBlocks.some((block: Element) => {
					const type = (block as HTMLElement).dataset.type
					return type === 'defaultTable' || type === 'image' || type === 'file'
				})


				if (hasSpecialBlock) {
					// .editor--container__content 래퍼로 감싸서 paste에서 인식 가능하게 함
					const editorContainer = document.createElement('div')
					editorContainer.classList.add('editor--container__content')
					selectedBlocks.forEach((block: Element) => {
						const clonedBlock = block.cloneNode(true) as HTMLElement
						// .editor--block__container로 감싸기
						const blockContainer = document.createElement('div')
						blockContainer.classList.add('editor--block__container')
						blockContainer.appendChild(clonedBlock)
						editorContainer.appendChild(blockContainer)
					})
					container.appendChild(editorContainer)
				} else {
					const clonedContents = range.cloneContents()
					container.appendChild(clonedContents)
				}
			} else {
				const clonedContents = range.cloneContents()
				container.appendChild(clonedContents)
			}
		}
		// 단일 블록 전체가 선택된 경우 (테이블, 이미지 등)
		// 단, 테이블 셀 내부의 텍스트만 선택된 경우는 제외
		else if (editorBlock && ((editorBlock as HTMLElement).dataset.type === 'defaultTable' || (editorBlock as HTMLElement).dataset.type === 'image' || (editorBlock as HTMLElement).dataset.type === 'file') && !isWithinSingleCell) {
			const clonedBlock = editorBlock.cloneNode(true) as HTMLElement
			container.appendChild(clonedBlock)
		}
		// 텍스트 노드만 선택된 경우 부모 span의 스타일 포함
		else if (isInsideBlock && isSpanElement && range.startContainer.nodeType === Node.TEXT_NODE) {
			// 선택된 텍스트만 가져오기
			const clonedContents = range.cloneContents()
			const selectedText = clonedContents.textContent || ''

			// 부모 span을 복제하고 선택된 텍스트만 넣기
			const styledSpan = ancestorElement.cloneNode(false) as HTMLElement
			styledSpan.textContent = selectedText
			container.appendChild(styledSpan)
		} else {
			// 기존 로직: range의 내용을 복사
			const clonedContents = range.cloneContents()
			container.appendChild(clonedContents)
		}

		// container의 자식들을 직접 처리하여 불필요한 래퍼 제거
		const processedChildren = Array.from(container.childNodes)
			.map(child => processNode(child))
			.join('')


		const tempDiv = document.createElement('div')
		tempDiv.innerHTML = processedChildren

		// .editor--container__content가 있으면 블록 구조 유지 (여러 블록 복사)
		const hasEditorContainer = tempDiv.querySelector('.editor--container__content')
		if (!hasEditorContainer) {
			// 단일 블록 복사 시에만 .editor--block__container 제거
			const blockContainers = tempDiv.querySelectorAll('.editor--block__container')
			blockContainers.forEach(container => {
				const parent = container.parentNode
				if (parent) {
					while (container.firstChild) {
						parent.insertBefore(container.firstChild, container)
					}
					parent.removeChild(container)
				}
			})
		}
		const finalHTML = tempDiv.innerHTML


		const plainText = tempDiv.textContent?.replace(INVISIBLE_RE, '') || ''


		if (event.clipboardData) {
			event.clipboardData.setData('text/html', finalHTML)
			event.clipboardData.setData('text/plain', plainText)
		}
	}

	function processNode(node: Node): string {
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.textContent || ''
			const cleanedText = text.replace(INVISIBLE_RE, '')
			return escapeHTML(cleanedText)
		}

		if (node.nodeType === Node.ELEMENT_NODE) {
			const element = node as HTMLElement

		if (element.tagName === 'SPAN') {
			if (element.hasAttribute('data-latex')) {
				if (element.querySelector('.katex')) {
					return element.outerHTML
				}
				const formula = element.getAttribute('data-formula')
					|| element.querySelector('annotation[encoding="application/x-tex"]')?.textContent
				if (formula) {
					try {
						const clone = element.cloneNode(false) as HTMLElement
						clone.innerHTML = katex.renderToString(formula, { throwOnError: false })
						return clone.outerHTML
					} catch { /* fallthrough */ }
				}
				return element.outerHTML
			}

			const span = element as HTMLSpanElement
			const styleModel = StyleModel.fromSpan(span)

			const textContent = Array.from(span.childNodes)
				.map(child => processNode(child))
				.join('')

			if (styleModel.isEmpty()) {
				return textContent
			} else {
				const newSpan = document.createElement('span')
				styleModel.applyToSpan(newSpan)
				newSpan.innerHTML = textContent
				return newSpan.outerHTML
			}
		}

			if (element.tagName === 'DIV' && element.classList.contains('editor--block')) {
				// .editor--block은 유지하여 붙여넣기 시 블록 구분 보존
				const processedChildren = Array.from(element.childNodes)
					.map(child => processNode(child))
					.join('')
				const clone = element.cloneNode(false) as HTMLElement
				// id와 tabindex는 제거 (붙여넣기 시 새로 생성됨)
				clone.removeAttribute('id')
				clone.removeAttribute('tabindex')
				clone.innerHTML = processedChildren
				return clone.outerHTML
			}

			// .editor--container__content는 유지 (여러 블록 복사 시)
			if (element.tagName === 'DIV' && element.classList.contains('editor--container__content')) {
				const processedChildren = Array.from(element.childNodes)
					.map(child => processNode(child))
					.join('')
				const clone = element.cloneNode(false) as HTMLElement
				clone.innerHTML = processedChildren
				return clone.outerHTML
			}

			// .editor--block__container는 유지 (여러 블록 복사 시)
			if (element.tagName === 'DIV' && element.classList.contains('editor--block__container')) {
				const processedChildren = Array.from(element.childNodes)
					.map(child => processNode(child))
					.join('')
				const clone = element.cloneNode(false) as HTMLElement
				clone.innerHTML = processedChildren
				return clone.outerHTML
			}

			const processedChildren = Array.from(element.childNodes)
				.map(child => processNode(child))
				.join('')

			const clone = element.cloneNode(false) as HTMLElement
			clone.innerHTML = processedChildren
			return clone.outerHTML
		}

		return ''
	}

	async function onCut(event: ClipboardEvent) {
		const cutTarget = event.target as HTMLElement
		const cutTableBlock = cutTarget.closest('.editor--block[data-type="defaultTable"]')
		if (cutTableBlock) {
			const { dragList } = storeToRefs(useEditorStore())
			if (dragList.value.length >= 2) {
				return
			}
		}

		// selectedTableImageFileBlocks 로직 추가
		let selection = window.getSelection()
		let selectedTableImageFileBlocks: HTMLElement[] = []
		
		if (selection && selection.rangeCount > 0) {
			const range = selection.getRangeAt(0)
			
			const editor = document.getElementById('editor')
			if (editor) {
				const allSpecialBlocks = editor.querySelectorAll('.editor--block[data-type="defaultTable"], .editor--block[data-type="image"], .editor--block[data-type="file"]')
				allSpecialBlocks.forEach((block: any) => {
					// DOM 위치 기반 검사
					try {
						const blockContainer = block.closest('.editor--block__container') || block
						
						// Range와 블록의 위치 비교
						const isInRange = selection!.containsNode(blockContainer, true) || 
						                   selection!.containsNode(block, true)
						
						// Ctrl+A 같은 에디터 전체 선택만 감지 (일부 선택은 제외)
						const rangeCommonAncestor = range.commonAncestorContainer
						const editorElement = document.getElementById('editor')
						const isEditorWideSelection = rangeCommonAncestor === editorElement
						
						if (isInRange || isEditorWideSelection) {
							selectedTableImageFileBlocks.push(block as HTMLElement)
						}
					} catch (e) {
					}
				})
			}
		}
		
		// 기존 코드 재확인
		selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) {
			return
		}

		const range = selection.getRangeAt(0)
		if (range.collapsed) {
			return
		}

		event.preventDefault()
		
		const history = new UseHistory()
		await history.push()

		const container = document.createElement('div')
		
		// commonAncestorContainer가 TEXT_NODE인 경우 부모 요소 확인
		let ancestorElement = range.commonAncestorContainer as HTMLElement
		if (ancestorElement.nodeType === Node.TEXT_NODE) {
			ancestorElement = ancestorElement.parentElement as HTMLElement
		}
		
		// ancestorElement가 span이고 .editor--block 내부이면 span을 포함하여 복사
		const editorBlock = ancestorElement?.closest('.editor--block')
		const isInsideBlock = !!editorBlock
		const isSpanElement = ancestorElement?.tagName === 'SPAN'
		
		// 여러 블록이 선택된 경우 또는 전체 블록이 선택된 경우 처리
		const startContainer = range.startContainer
		const endContainer = range.endContainer
		let startBlock = (startContainer.nodeType === Node.ELEMENT_NODE ? (startContainer as HTMLElement) : (startContainer.parentElement as HTMLElement))?.closest('.editor--block')
		let endBlock = (endContainer.nodeType === Node.ELEMENT_NODE ? (endContainer as HTMLElement) : (endContainer.parentElement as HTMLElement))?.closest('.editor--block')
		
		// selectedTableImageFileBlocks가 있으면 실제 선택 범위 재계산
		if (selectedTableImageFileBlocks.length > 0) {
			const allBlocks = Array.from(document.querySelectorAll('.editor--block'))
			const allSelectedBlocks = [...selectedTableImageFileBlocks]
			
			// startBlock과 endBlock이 있으면 포함
			if (startBlock && !allSelectedBlocks.includes(startBlock as HTMLElement)) {
				allSelectedBlocks.push(startBlock as HTMLElement)
			}
			if (endBlock && !allSelectedBlocks.includes(endBlock as HTMLElement)) {
				allSelectedBlocks.push(endBlock as HTMLElement)
			}
			
			const indices = allSelectedBlocks
				.map(block => allBlocks.indexOf(block as Element))
				.filter(idx => idx !== -1)
			
			if (indices.length > 0) {
				const minIdx = Math.min(...indices)
				const maxIdx = Math.max(...indices)
				startBlock = allBlocks[minIdx] as HTMLElement
				endBlock = allBlocks[maxIdx] as HTMLElement
			}
		}
		
		// 테이블 셀 내부 텍스트 선택 여부 확인
		const startElement = startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentElement : startContainer as HTMLElement
		const endElement = endContainer.nodeType === Node.TEXT_NODE ? endContainer.parentElement : endContainer as HTMLElement
		const startCell = startElement?.closest('td')
		const endCell = endElement?.closest('td')
		const isWithinSingleCell = startCell && endCell && startCell === endCell
		
		// 여러 블록이 선택된 경우
		if (startBlock && endBlock && startBlock !== endBlock) {
			const allBlocks = Array.from(document.querySelectorAll('.editor--block'))
			const startIdx = allBlocks.indexOf(startBlock as Element)
			const endIdx = allBlocks.indexOf(endBlock as Element)
			
			if (startIdx !== -1 && endIdx !== -1) {
				const selectedBlocks = allBlocks.slice(Math.min(startIdx, endIdx), Math.max(startIdx, endIdx) + 1)
				const hasSpecialBlock = selectedBlocks.some((block: Element) => {
					const type = (block as HTMLElement).dataset.type
					return type === 'defaultTable' || type === 'image' || type === 'file'
				})
				
				if (hasSpecialBlock) {
					// .editor--container__content 래퍼로 감싸서 paste에서 인식 가능하게 함
					const editorContainer = document.createElement('div')
					editorContainer.classList.add('editor--container__content')
					selectedBlocks.forEach((block: Element) => {
						const clonedBlock = block.cloneNode(true) as HTMLElement
						// .editor--block__container로 감싸기
						const blockContainer = document.createElement('div')
						blockContainer.classList.add('editor--block__container')
						blockContainer.appendChild(clonedBlock)
						editorContainer.appendChild(blockContainer)
					})
					container.appendChild(editorContainer)
				} else {
					const clonedContents = range.cloneContents()
					container.appendChild(clonedContents)
				}
			} else {
				const clonedContents = range.cloneContents()
				container.appendChild(clonedContents)
			}
		}
		// 단일 블록 전체가 선택된 경우 (테이블, 이미지 등)
		// 단, 테이블 셀 내부의 텍스트만 선택된 경우는 제외
		else if (editorBlock && ((editorBlock as HTMLElement).dataset.type === 'defaultTable' || (editorBlock as HTMLElement).dataset.type === 'image' || (editorBlock as HTMLElement).dataset.type === 'file') && !isWithinSingleCell) {
			const clonedBlock = editorBlock.cloneNode(true) as HTMLElement
			container.appendChild(clonedBlock)
		}
		// 텍스트 노드만 선택된 경우 부모 span의 스타일 포함
		else if (isInsideBlock && isSpanElement && range.startContainer.nodeType === Node.TEXT_NODE) {
			// 선택된 텍스트만 가져오기
			const clonedContents = range.cloneContents()
			const selectedText = clonedContents.textContent || ''
			
			// 부모 span을 복제하고 선택된 텍스트만 넣기
			const styledSpan = ancestorElement.cloneNode(false) as HTMLElement
			styledSpan.textContent = selectedText
			container.appendChild(styledSpan)
		} else {
			// 기존 로직: range의 내용을 복사
			const clonedContents = range.cloneContents()
			container.appendChild(clonedContents)
		}

		// container의 자식들을 직접 처리하여 불필요한 래퍼 제거
		const processedChildren = Array.from(container.childNodes)
			.map(child => processNode(child))
			.join('')
		
		const tempDiv = document.createElement('div')
		tempDiv.innerHTML = processedChildren
		
		// .editor--container__content가 있으면 블록 구조 유지 (여러 블록 복사)
		const hasEditorContainer = tempDiv.querySelector('.editor--container__content')
		if (!hasEditorContainer) {
			// 단일 블록 복사 시에만 .editor--block__container 제거
			const blockContainers = tempDiv.querySelectorAll('.editor--block__container')
			blockContainers.forEach(container => {
				const parent = container.parentNode
				if (parent) {
					while (container.firstChild) {
						parent.insertBefore(container.firstChild, container)
					}
					parent.removeChild(container)
				}
			})
		}
		const finalHTML = tempDiv.innerHTML
		
		const plainText = tempDiv.textContent?.replace(INVISIBLE_RE, '') || ''

		if (event.clipboardData) {
			event.clipboardData.setData('text/html', finalHTML)
			event.clipboardData.setData('text/plain', plainText)
		}
		
		// Cut: 선택된 내용 삭제
		range.deleteContents()
		
		// white-space: normal에서 연속 공백이 축소되지 않도록 &nbsp;로 대체
		preserveSpacesAfterDelete()
		
		// 여러 블록이 선택된 경우 빈 블록 제거
		if (startBlock && endBlock && startBlock !== endBlock) {
			const allBlocks = Array.from(document.querySelectorAll('.editor--block'))
			const startIdx = allBlocks.indexOf(startBlock as Element)
			const endIdx = allBlocks.indexOf(endBlock as Element)
			
			if (startIdx !== -1 && endIdx !== -1) {
				const selectedBlocks = allBlocks.slice(Math.min(startIdx, endIdx), Math.max(startIdx, endIdx) + 1)
				
				// 첫 번째 블록을 제외한 나머지 블록들 제거
				for (let i = 1; i < selectedBlocks.length; i++) {
					const block = selectedBlocks[i] as HTMLElement
					const container = block.closest('.editor--block__container')
					if (container) {
						container.remove()
					} else {
						block.remove()
					}
				}
				
				// 첫 번째 블록에 포커스
				const firstBlock = selectedBlocks[0] as HTMLElement
				if (firstBlock) {
					firstBlock.focus()
					
					// 커서를 블록의 시작 위치로 이동
					const selection = window.getSelection()
					if (selection) {
						const range = document.createRange()
						range.selectNodeContents(firstBlock)
						range.collapse(true)
						selection.removeAllRanges()
						selection.addRange(range)
					}
				}
			}
		}
		
		// DOM 변경 후 블록 content 업데이트 및 input 이벤트 트리거
		const blockEl = ancestorElement?.closest('.editor--block') as HTMLElement
		if (blockEl) {
			// Input 이벤트를 트리거하여 history 저장이 자동으로 이루어지도록 함
			const inputEvent = new Event('input', { bubbles: true })
			blockEl.dispatchEvent(inputEvent)
		}
	}

	return {
		onCopy,
		onCut
	}
}
